import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react';
import type { CSSProperties } from 'react';

/**
 * Semantic icon sizes.
 *
 * A fixed scale, not free-form numbers. Icons that differ by two pixels look
 * like a mistake rather than a decision, and an icon that does not match its
 * adjacent text size is the most common source of misaligned rows.
 */
export const ICON_SIZES = {
  xs: 14,
  sm: 16,
  md: 18,
  lg: 20,
  xl: 24,
  '2xl': 32,
} as const;

export type IconSize = keyof typeof ICON_SIZES;

export interface IconProps {
  icon: IconSvgElement;
  size?: IconSize;
  /** Defaults to `currentColor`, so icons inherit the text colour around them. */
  color?: string;
  strokeWidth?: number;
  className?: string;
  style?: CSSProperties;
  /**
   * Describes the icon for screen readers. Omit for purely decorative icons
   * sitting beside a text label — announcing "calendar, Leave" is noise.
   */
  label?: string;
}

/**
 * The single way to render an icon.
 *
 * Wrapping HugeIcons rather than importing it directly in every feature gives
 * us one place to fix stroke weight, one place to enforce the size scale, and
 * one place to get the accessibility right. It also means swapping icon sets
 * later is one file, not two hundred.
 *
 * Stroke weight 1.5 is deliberate: HugeIcons' default 2 reads heavy next to
 * Mona Sans at 14px, and the icons end up looking bolder than the text they
 * label.
 */
export function Icon({
  icon,
  size = 'md',
  color = 'currentColor',
  strokeWidth = 1.5,
  className,
  style,
  label,
}: IconProps) {
  return (
    <HugeiconsIcon
      icon={icon}
      size={ICON_SIZES[size]}
      color={color}
      strokeWidth={strokeWidth}
      className={className}
      style={{ flexShrink: 0, display: 'block', ...style }}
      aria-hidden={label ? undefined : true}
      aria-label={label}
      role={label ? 'img' : undefined}
    />
  );
}
